import React, { useEffect, useState, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { toast } from 'react-toastify';
import logger from '../utils/logger';
import '../styles/table.css';

const speak = (text, rate = 0.9) => {
  if (!text) return;
  const synth = window.speechSynthesis;
  synth.cancel();

  const voices = synth.getVoices();
  const femaleVoice =
    voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") ||
        v.name.toLowerCase().includes("anya") ||
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("susan"))
    ) || voices.find(v => v.lang === "en-US");

  const utter = new SpeechSynthesisUtterance(text);
  utter.voice = femaleVoice;
  utter.lang = "en-US";
  utter.rate = rate;

  setTimeout(() => synth.speak(utter), 200);
};

if (window.speechSynthesis.onvoiceschanged !== undefined) {
  window.speechSynthesis.onvoiceschanged = () => {
    window.speechSynthesis.getVoices();
  };
}
const ConfirmToast = ({ message, onConfirm, onCancel }) => {
  return (
    <div  style={{ maxWidth: '600px' }}>
  <div className="mb-3">
    <h6 className="mb-0 text-center meassage-text">{message}</h6>
  </div>
  <div className="d-flex justify-content-center gap-2">

   
    <Button  className='yes-button' size="sm" onClick={onConfirm}>
      Yes
    </Button>
     <Button  className='No-button' size="sm" onClick={onCancel}>
      No
    </Button>
  </div>
</div>

  );
};

const Group = () => {
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({ name: '' });
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef();
  const hasSpokenCloseRef = useRef(false);
const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] = useState(false);
  const BASE_ENDPOINT = '/Group';

  useEffect(() => {
    const timer = setTimeout(() => {
      speak("Welcome to Group Management. You can add, edit or delete groups here.");
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    try {
      logger.debug('Fetching groups...');
      const response = await apiGet(`${BASE_ENDPOINT}/GetAll`);
      if (response?.data?.length) {
        const sortedData = [...response.data].sort((a, b) => b.groupID - a.groupID);
        setGroups(sortedData);
        logger.debug('Groups fetched:', sortedData);
      } else {
        setGroups([]);
        logger.debug('No groups found.');
      }
    } catch (error) {
      logger.error('Failed to fetch groups:', error);
      toast.error(<div><strong>Failed to load groups</strong><div>{error.message}</div></div>, { autoClose: 7000 });
      speak("Failed to load groups. Please try again.");
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus();
      speak(editId ? "Editing group." : "Adding new group.");
    }
  }, [isModalOpen, editId]);

  const openModal = (data = { name: '' }) => {
    setFormData(data);
    setIsModalOpen(true);
    hasSpokenCloseRef.current = false;
  };

  const closeModal = () => {
    setFormData({ name: '' });
    setEditId(null);
    setIsModalOpen(false);
    if (!hasSpokenCloseRef.current) {
      speak("Form closed.");
      hasSpokenCloseRef.current = true;
    }
  };

  const sanitizeGroupName = (input) => {
    return input.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').trim();
  };

  const handleSpeechInput = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      speak("Listening...");
    };

    recognition.onerror = () => {
      setIsListening(false);
      speak("Sorry, I didn't catch that. Please try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      transcript = sanitizeGroupName(transcript);
      setFormData({ name: transcript });
      speak(`You said: ${transcript}`);
    };

    recognition.start();
  };

 



  const handleSubmit = async (e) => {
  e.preventDefault();
  const trimmedName = sanitizeGroupName(formData.name?.trim());

  if (!trimmedName) {
    const msg = 'Group name is required!';
    toast.error(msg);
    speak(msg);
    return;
  }

  setIsLoading(true);
  const payload = { name: trimmedName };

  try {
    let msg = '';
    if (editId) {
      await apiPut(`${BASE_ENDPOINT}/${editId}`, { ...payload, groupID: editId });
      msg = 'Group updated successfully!';
    } else {
      await apiPost(`${BASE_ENDPOINT}/Create`, payload);
      msg = 'Group added successfully!';
    }

    toast.success(msg);
    setTimeout(() => speak(msg), 300); 
    await fetchGroups();
    closeModal();
  } catch (error) {
    handleApiError(error);
  } finally {
    setIsLoading(false);
  }
};


  const handleApiError = (error) => {
    logger.error('API Error:', error);
    const statusCode = error?.response?.status;
    const apiMessage = error?.response?.data?.status;

    const errorMessages = {
      400: apiMessage || 'Invalid group data',
      404: 'Group not found',
      409: 'Group name already exists',
      500: 'Internal server error'
    };

    const errorMessage = errorMessages[statusCode] || 'Something went wrong';
    const fullMessage = `Operation Failed. ${errorMessage}`;
    toast.error(<strong>{fullMessage}</strong>, { autoClose: 7000 });
    speak(fullMessage);
  };

  const handleEdit = (row) => {
    logger.debug('Editing group:', row);
    setEditId(row.groupID);
    openModal({ name: row.name });
  };

 


  const handleDelete = async (id) => {
     if (isDeleteConfirmationActive) return;
    const groupToDelete = groups.find(g => g.groupID === id);
    const confirmMsg = `Are you sure you want to delete the group`;
  setIsDeleteConfirmationActive(true);
   toast.info(
  <ConfirmToast
    message={confirmMsg}
    onConfirm={async () => {
      toast.dismiss();
      try {
        setIsLoading(true);
        await apiDelete(`${BASE_ENDPOINT}/${id}`);
        const msg = 'Group deleted successfully!';
        toast.success(<strong>{msg}</strong>, { autoClose: 5000, icon: false }); // icon disabled here
        speak(msg);
        await fetchGroups();
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
        setIsDeleteConfirmationActive(false);
      }
    }}
    onCancel={() => {
      toast.dismiss();
      speak("Delete cancelled.");
       setIsDeleteConfirmationActive(false); 
    }}
  />,
  {
   position: "top-center",
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    closeButton: false,
    icon: false, 
   
    className: 'custom-toast', 
    bodyClassName: 'custom-toast-body', 
    hideProgressBar: true,
  }
);

  };


  const columns = [
    {
      name: 'ID',
      selector: row => row.groupID,
      sortable: true,
      omit: true,
    },
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      cell: row => <span className="text-capitalize">{row.name}</span>,
         width: '600px',
   center:true,
     style: {
    borderRight: '1px solid #e5e8ebff!important', 
  },
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-warning custompadding" size="sm me-2"
            onClick={() => handleEdit(row)}
            onFocus={() => speak(`Edit ${row.name} group`)}>
            <i className="bi bi-pencil"></i>
          </Button>
          <Button variant="outline-danger custompadding" size="sm me-2"
            onClick={() => handleDelete(row.groupID)}
            onFocus={() => speak(`Delete ${row.name} group`)}
               disabled={isDeleteConfirmationActive}>
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      ),
     width: '450px',
     center:true,
    },
  ];

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 page-heading">Group</h2>
        <Button variant="btn-primary" className="btn-fontweight"
          onClick={() => openModal()} disabled={isLoading}>
          {isLoading ? <Spinner size="sm" /> : 'Add Group'}
        </Button>
      </div>

      {isLoading && !groups.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="btn-primary" />
          <p className="mt-2">Loading groups...</p>
        </div>
      ) : (
        <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white mb-custom">
          <DataTable
         
            columns={columns}
        
                          data={groups.map((dept, idx) => ({
  ...dept,
    _rowIndex: idx, 
  }))}
            pagination
        
            striped
            responsive
              className="custom-data-table department-table"
            customStyles={{
              table: {
                style: {
                     borderRight: '1px solid #dee2e6',
      fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "uppercase",
                },
              },
              rows: {
                style: {
                  borderBottom: "1px solid #fdfdfd",
                  "&:hover": {
                    backgroundColor: "#fdfdfd",
                  },
                },
              },
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
                               conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}
            noDataComponent={<Alert variant="info">No groups found</Alert>}
            progressPending={isLoading}
            progressComponent={<Spinner animation="border" variant="btn-primary" />}
            onRowClicked={(row) => speak(`Group: ${row.name}`)}
          />
        </div>
      )}

      <Modal show={isModalOpen} onHide={closeModal} backdrop="static">
        <Modal.Header closeButton className="modal-customheader">
          <Modal.Title className="custom-modaltitle">
            {editId ? 'Edit Group' : 'Add Group'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-1">
              <Form.Label className='mb-2'>Group Name</Form.Label>
              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  ref={inputRef}
                  value={formData.name}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                    setFormData({ ...formData, name: onlyLetters });
                  }}
                  placeholder="Enter group name"
                  required
                  disabled={isLoading}
                />
                <Button
                  variant={isListening ? "danger" : "outline-secondary"}
                  onClick={handleSpeechInput}
                  disabled={isLoading}
                >
                  <i className={`bi bi-mic${isListening ? "-fill" : ""}`}></i>
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="custommodal-footer pb-3">
            <Button
              variant="secondary"
              className="btn-fontweight cancelbtn-fontsize"
              onClick={closeModal}
              disabled={isLoading}
              onFocus={() => speak("Cancel button")}
            >
              Cancel
            </Button>
            <Button
              variant="btn-primary"
              className="btn-fontweight addbtn-fontsize"
              type="submit"
              disabled={isLoading}
              onFocus={() => speak(editId ? "Update group button" : "Add group button")}
            >
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {editId ? 'Updating...' : 'Adding...'}
                </>
              ) : editId ? 'Update Group' : 'Add Group'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Group;
